import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { BusinessProfileProvider } from '../../contexts/BusinessProfileContext';
import BusinessProfileSelector from '../BusinessProfileSelector';
import { Toaster } from 'sonner';

const navigation = [
  { name: 'Voice Agent', href: '/cloud/voice', icon: 'microphone' },
];

const icons = {
  microphone: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  ),
  creditCard: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  cog: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

export default function CloudLayout() {
  const { user, logout } = useCloudAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/cloud/login');
  };

  return (
    <div className="h-screen bg-[#0a0a0b] flex overflow-hidden">
      {/* Sidebar — fixed height, no overflow scroll */}
      <div className="w-64 bg-[#111113] border-r border-gray-800 flex flex-col flex-shrink-0 h-full overflow-y-auto">
        {/* Logo */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <div>
              <h1 className="text-white font-semibold">Ruonn Cloud</h1>
              <p className="text-xs text-gray-500">Voice Agent Platform</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navigation.map((item) => (
              <li key={item.name}>
                {item.disabled ? (
                  <span className="flex items-center gap-3 px-3 py-2 text-gray-600 cursor-not-allowed rounded-lg">
                    {icons[item.icon]}
                    <span>{item.name}</span>
                    <span className="ml-auto text-xs">(Soon)</span>
                  </span>
                ) : (
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-purple-600/20 text-purple-400'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }`
                    }
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {icons[item.icon]}
                    <span>{item.name}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* User Menu */}
        <div className="p-4 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-sm text-white">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{user?.email}</p>
              <p className="text-xs text-gray-500">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full py-2 px-3 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-left flex items-center gap-2"
            data-testid="logout-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign out
          </button>
        </div>
      </div>

      {/* Main Content — takes remaining space, scrolls independently */}
      <BusinessProfileProvider>
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Top Header with Business Profile Selector */}
          <div className="bg-[#111113] border-b border-gray-800 px-6 py-3 flex items-center justify-end flex-shrink-0">
            <BusinessProfileSelector />
          </div>
          
          <div className="flex-1 overflow-auto">
            <Outlet />
          </div>
        </div>
      </BusinessProfileProvider>
      <Toaster richColors position="top-right" />
    </div>
  );
}
